// Agent ticket creation — route /tickets/drafts/:draftId, one server-saved draft per address
// (`/tickets/new` mints one and redirects here: pages/tickets/NewDraft.jsx).
//
// ~44 tickets a day are typed here while a customer is on the phone, which makes
// this screen's speed part of call handling time (SPEC.md §6). Everything below is
// arranged around that:
//
//   * Keyboard-first. The account-number field takes focus on arrival, tab order
//     follows the order of a call (who is calling → what happened → how it is
//     filed), Ctrl/⌘+Enter submits from anywhere, and Ctrl/⌘+Shift+Enter creates
//     and solves in one action. Nothing on the screen traps focus.
//   * One filing per call. POST /api/ticket-drafts/:id/file takes the main ticket, its
//     children (the (+) "another issue from this call") and its related-ticket links
//     together and creates them all in ONE transaction, or none (docs/API.md §12.5), so a
//     refusal never leaves a parent without some of its children.
//   * A draft that survives. Everything typed is saved to the server 400 ms after the
//     last change (components/drafts/draft-sync.js), with a write-ahead copy in this
//     browser keyed by user and draft (draft-buffer.js) that is replayed on the next load,
//     so a crash, a reload, a network blip or an expired session loses nothing. Several
//     drafts can be open at once, one tab each (docs/superpowers/specs/2026-10-13-multi-
//     ticket-drafts-design.md).
//
// The draft-specific decisions live in pure modules with tests: components/drafts/
// draft-screen.js (what a new, loaded, replayed or kept draft looks like, and which form,
// queue and owner the agent CHOSE — `touched`, kept per child too; the rest are only
// displayed with today's default and resolve at filing) and draft-flow.js (the filing
// body, mapping a refusal back onto the form, the notices, the stale-configuration check).
//
// Config (forms, users, queues, tags, macros, statuses) loads per-request and
// degrades: a lookup that fails leaves its picker empty and the ticket still
// creatable, rather than taking the screen down over a dropdown.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { api } from '../../api/client.js';
import { useSession, useCanWrite, useSessionEnded } from '../../session/SessionProvider.jsx';
import Modal from '../../components/Modal.jsx';
import DraftNotices from '../../components/drafts/DraftNotices.jsx';
import CallerPanel from '../../components/create/CallerPanel.jsx';
import Composer from '../../components/create/Composer.jsx';
import {
  visibleFieldsWithRequirement, valuesForVisibleFields,
} from '../../components/create/form-conditions.js';
import CustomFields, {
  encodeCustomFields,
  invalidNumberFields,
  missingRequiredFields,
} from '../../components/create/CustomFields.jsx';
import CustomerContext from '../../components/create/CustomerContext.jsx';
import TagInput from '../../components/create/TagInput.jsx';
import ChipSelect from '../../components/create/ChipSelect.jsx';
import { fillMacroTemplate } from '../../components/create/macro-placeholders.js';
import { prefillFromParent, assigneeFor } from '../../components/create/child-prefill.js';
import { typeOptionsFor } from './ticket-type.js';
import TicketLinkPicker from '../../components/tickets/TicketLinkPicker.jsx';
import ChildTicketCard from '../../components/create/ChildTicketCard.jsx';
import {
  childBody, createLabel, newChildDraft, nextChildKey, validateChild,
} from '../../components/create/child-tickets.js';
import { useDrafts } from '../../components/drafts/DraftsProvider.jsx';
import { childTouched, toSaveBody } from '../../components/drafts/draft-model.js';
import { browserStorage, createBuffer } from '../../components/drafts/draft-buffer.js';
import { createDraftSync } from '../../components/drafts/draft-sync.js';
import { readLegacySlot, clearLegacySlot } from '../../components/drafts/legacy-slot.js';
import {
  EMPTY_CALLER, EMPTY_COMPOSER, draftHasContent, initialScreen, mergeScreen, screenFromDraft,
  screenFromBody, addTouched, withConfigDefaults, withChildConfigDefaults, prefillTouched,
  callerFromCustomer, legacyKeep,
  saveKey, unsavedText, callerNeedsDetail, hydrateCaller, bufferAgainstServer, legacyKeepReady,
} from '../../components/drafts/draft-screen.js';
import {
  fileRequest, mapFilingError, replayOutcome, syncNotice, noticeForError, tabStatus,
  staleChoicesFor, blockingReason, nextDraftHref, isDraftAddress, saveStatusLabel, replayNotice,
  unmountPlan,
} from '../../components/drafts/draft-flow.js';
import { supportPath } from '../../support-path.js';

const EMPTY_CONFIG = {
  loaded: false,
  failed: [],
  forms: [],
  assignees: [],
  users: [],
  queues: [],
  tags: [],
  macros: [],
  statuses: [],
};

// Loaded together, each independently: one 404 from an endpoint another agent has
// not landed yet must not blank the screen.
const CONFIG_REQUESTS = [
  ['forms', '/ticket-forms'],
  ['assignees', '/users?can_be_assigned=true'],
  ['users', '/users'],
  ['queues', '/queues'],
  ['tags', '/tags'],
  ['macros', '/macros'],
  ['statuses', '/ticket-statuses'],
];

const BASE_STATUSES = [
  ['new', 'New'],
  ['open', 'Open'],
  ['pending', 'Pending'],
  ['on_hold', 'On hold'],
  ['solved', 'Solved'],
  ['closed', 'Closed'],
];

const PRIORITIES = [
  ['low', 'Low'],
  ['normal', 'Normal'],
  ['high', 'High'],
  ['urgent', 'Urgent'],
];

// A save that failed because the server could not be reached is tried again on `online`,
// on window focus, and on this slow timer.
const RETRY_MS = 20 * 1000;

// The latest load of each draft in this browser tab, by draft id. A screen that unmounts
// with a save still waiting reports how that save ended only if no newer load of the SAME
// draft (the agent switching back to its tab) has started since.
const latestLoad = new Map();

export default function TicketNew() {
  const user = useSession();
  const canWrite = useCanWrite();
  const sessionEnded = useSessionEnded();
  const navigate = useNavigate();
  const location = useLocation();
  // App.jsx mounts this screen keyed by the draft id, so the id never changes under it.
  const { draftId } = useParams();
  const { drafts, refresh, reportStatus, announceFiled } = useDrafts();
  const defaultAssigneeId = assigneeFor(user);

  const [storage] = useState(browserStorage);
  const [buffer] = useState(() => createBuffer(storage));
  // A way in (`?parent=`, `?customer=`) arrives ONCE, as router state from /tickets/new.
  // Read on the first render only and cleared from the history entry once applied, so a
  // reload of this address never applies it again.
  const prefillRef = useRef(location.state?.prefill ?? null);

  const [caller, setCaller] = useState(EMPTY_CALLER);
  const [fields, setFields] = useState(() => initialScreen(user).fields);
  const [composer, setComposer] = useState(EMPTY_COMPOSER);
  // The form, queue and owner the agent CHOSE (draft-screen.js). Anything else among those
  // three is displayed with today's default and left out of what is saved and filed.
  const [touched, setTouched] = useState([]);

  // 'loading' → 'ready'; 'closed' when the draft was already filed, discarded or taken over
  // (there is no form to show); 'error' when it could not be read at all.
  const [phase, setPhase] = useState('loading');
  const [loadError, setLoadError] = useState(null);
  // Bumped to read the draft again: after a stale save, or a filing refused as stale.
  const [loadKey, setLoadKey] = useState(0);
  // The save controller's state (draft-sync.js), as last reported.
  const [snapshot, setSnapshot] = useState(null);
  // Whether the server already held this draft when the screen opened (the initial GET
  // found it). Fixed for the sitting: the autosave a new draft makes within a moment does
  // not change it.
  const [loadedFromServer, setLoadedFromServer] = useState(false);
  // The draft was closed elsewhere (filed, discarded, taken over) as a 409 told us on load,
  // discard or filing: a draft-flow.js notice. The form is read-only then.
  const [closed, setClosed] = useState(null);
  // Typing that could not be saved, shown ONCE (it is not kept anywhere): `{ heading, text }`.
  const [unsaved, setUnsaved] = useState(null);
  // The old browser slot's draft, offered once (Keep / Discard) on a new draft.
  const [legacy, setLegacy] = useState(null);
  const [prefillParent, setPrefillParent] = useState(null);
  const [parentState, setParentState] = useState({ status: 'idle', error: null });
  const [prefillCustomer, setPrefillCustomer] = useState(null);
  const [customerState, setCustomerState] = useState({ status: 'idle', error: null });
  // The draft's store as it is NOW (`{ id, is_active }`), for the retired-store check.
  const [storeRow, setStoreRow] = useState(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [people, setPeople] = useState({ orgId: null, rows: [], status: 'idle', error: null });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const childSubjectRefs = useRef(new Map());

  const syncRef = useRef(null);
  // The save key (draft-screen.js) of what the server holds or the controller was last
  // handed: a render whose state has the same key has nothing new to save.
  const lastKeyRef = useRef(null);
  // Filed or discarded from this screen: nothing is left to flush on the way out.
  const finishedRef = useRef(false);
  const mountedRef = useRef(false);
  // True from the moment Create is pressed until filing fails: no edit is saved, and the
  // form takes no input, so what is filed is exactly what was saved.
  const submittingRef = useRef(false);

  const searchRef = useRef(null);
  const subjectRef = useRef(null);
  const errorRef = useRef(null);
  const submitRef = useRef(null);
  const keepEditingRef = useRef(null);
  const formRef = useRef(null);
  const noticeRef = useRef(null);
  const wasReadOnly = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  function applyScreen(screen) {
    setCaller(screen.caller);
    setFields(screen.fields);
    setComposer(screen.composer);
    setTouched(screen.touched);
  }

  // LOADING THE DRAFT, and the one save controller behind it.
  //
  // 1. A buffered edit left in this browser (keyed by the signed-in user and this draft) is
  //    replayed FIRST, against the revision it was built on, as the spec asks. Taken, it is
  //    gone from the buffer; refused as stale or taken over, its text is shown once; refused
  //    for any other reason (offline, too large), it stays and fills the form below, and the
  //    controller keeps trying.
  // 2. GET the draft. 404 is a new draft: an empty screen, the one-time prefill, or the old
  //    browser slot's offer. A 409 means it was closed elsewhere.
  // 3. One createDraftSync, reporting every state change to the tab row.
  // On unmount (another tab opened, or the page left) a pending save is flushed — never
  // after a sign-out — and the tab row forgets this draft's state.
  useEffect(() => {
    if (!canWrite) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const path = `/ticket-drafts/${draftId}`;
    const stopped = (error) => cancelled || error?.name === 'AbortError';
    const token = {};
    latestLoad.set(draftId, token);

    (async () => {
      setPhase('loading');
      let entry = buffer.read(user.id, draftId);
      let refused = null; // { outcome, body }: a replay the server would not take
      if (entry) {
        try {
          await api.put(path, { ...entry.body, base_revision: entry.baseRevision }, { signal: controller.signal });
          buffer.remove(user.id, draftId);
          entry = null;
        } catch (error) {
          if (stopped(error)) return;
          const outcome = replayOutcome(error);
          if (outcome !== 'keep') {
            refused = { outcome, body: entry.body };
            buffer.remove(user.id, draftId);
            entry = null;
          }
        }
      }

      let draft = null;
      try {
        draft = (await api.get(path, { signal: controller.signal }))?.data ?? null;
      } catch (error) {
        if (stopped(error)) return;
        const notice = noticeForError(error);
        if (notice) {
          setClosed(notice);
          // Typing this browser still held for it can no longer be saved: shown once (not for
          // a draft the replay already found filed or discarded) and dropped.
          const lost = refused ?? (entry ? { outcome: 'keep', body: entry.body } : null);
          if (entry) buffer.remove(user.id, draftId);
          const text = lost ? unsavedText(lost.body) : '';
          if (text && lost.outcome !== 'closed') setUnsaved(replayNotice(lost.outcome, text));
          reportStatus(draftId, null);
          setPhase('closed');
          return;
        }
        if (error?.status !== 404) {
          setLoadError(error);
          setPhase('error');
          return;
        }
        // 404: not saved yet (a new draft), or not this person's. Either way a fresh screen,
        // which saves under this id from base revision 0.
        reportStatus(draftId, null);
      }
      if (cancelled) return;
      setLoadedFromServer(Boolean(draft));

      // A buffered edit the replay could not land (a 5xx, a refused shape) is kept only if it
      // was built on the revision the server holds. One built on an older revision would be
      // rebased over another PC's save and overwrite it, so it is shown once instead.
      const against = bufferAgainstServer(entry, draft);
      if (against === 'conflict' || against === 'gone') {
        refused = { outcome: against, body: entry.body };
        buffer.remove(user.id, draftId);
        entry = null;
      }

      const serverScreen = draft ? screenFromDraft(draft, user) : initialScreen(user);
      const serverKey = saveKey(toSaveBody(serverScreen));
      if (refused && saveKey(refused.body) !== serverKey) {
        // A refused replay whose text the server already holds (a save that landed just
        // before a crash, never acknowledged here) has nothing to show.
        const shown = replayNotice(refused.outcome, unsavedText(refused.body));
        if (shown) setUnsaved(shown);
      }

      const prefill = prefillRef.current;
      prefillRef.current = null;
      if (prefill) navigate(location.pathname, { replace: true, state: null });

      const sync = createDraftSync({
        // Never sent once this tab has signed out, so a waiting debounce, or the second save
        // of an unmount flush, cannot go out during the sign-out round trip.
        save: (body) => (sessionEnded()
          ? Promise.reject(Object.assign(new Error('Signed out: the draft was not saved.'), { status: 401, code: 'SIGNED_OUT' }))
          : api.put(path, body).then((response) => response?.data)),
        buffer,
        userId: user.id,
        draftId,
        revision: draft?.revision ?? 0,
        onState: (next) => {
          if (syncRef.current !== sync) return;
          setSnapshot(next);
          reportStatus(draftId, tabStatus(next));
        },
        onCallbackError: (error) => console.error(error),
      });
      syncRef.current = sync;
      setSnapshot(sync.getState());
      reportStatus(draftId, sync.getState());

      // What the server holds is the baseline: a screen that differs from it (a kept buffer,
      // a prefill, the agent typing) is an edit, and the effect below hands it to `sync`.
      lastKeyRef.current = serverKey;
      applyScreen(entry ? screenFromBody(entry.body, draft, user) : serverScreen);
      if (!draft && !entry) {
        if (prefill?.parent) setPrefillParent(prefill.parent);
        if (prefill?.customer) setPrefillCustomer(prefill.customer);
        if (!prefill && !refused) setLegacy(readLegacySlot(storage, { defaultAssigneeId }));
      }
      setPhase('ready');
    })();

    return () => {
      cancelled = true;
      controller.abort();
      const sync = syncRef.current;
      syncRef.current = null;
      const plan = sync
        ? unmountPlan(sync.getState(), { finished: finishedRef.current, ended: sessionEnded() })
        : 'drop';
      if (plan === 'flush') {
        // Saves what is waiting, then lets the controller go. Until it settles the tab keeps
        // its marker; then it shows how the save ended (a failed one keeps its warning, since
        // the text then exists only in this browser), unless a newer load of this draft has
        // started since.
        void sync.flush().then((final) => {
          sync.dispose();
          if (latestLoad.get(draftId) === token) reportStatus(draftId, tabStatus(final));
          void refresh();
        });
      } else {
        sync?.dispose();
        reportStatus(draftId, null);
      }
    };
    // Once per draft and per reload (`loadKey`). `user`, `buffer` and the provider's
    // callbacks are stable for the life of this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, loadKey, canWrite]);

  const notice = closed ?? syncNotice(snapshot);
  const readOnly = Boolean(notice?.readOnly);

  // Every change the agent makes goes to the save controller, which debounces it, writes
  // it to the buffer first, and saves it. Skipped while nothing differs from what was
  // last handed over (a render, or a default filled in for an untouched key).
  function pushEdit() {
    const sync = syncRef.current;
    if (!sync || readOnly || submittingRef.current) return;
    const body = toSaveBody({ caller, fields, composer, touched });
    const key = saveKey(body);
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    sync.edit(body);
  }

  useEffect(() => {
    if (phase === 'ready') pushEdit();
  });

  // A stale save: the server's copy is read again (a new controller on its revision), and
  // the text that could not be saved is shown once. Taken over: the text is shown once and
  // the form is read-only. Either way the buffer entry is dropped once shown.
  useEffect(() => {
    if (snapshot?.state !== 'stale' && snapshot?.state !== 'taken') return;
    const text = unsavedText(snapshot.unsaved);
    if (snapshot.state === 'stale') {
      setUnsaved({ heading: 'This draft was changed on another PC, so it has been reloaded. What was typed here could not be saved:', text });
      setLoadKey((key) => key + 1);
    } else if (text) {
      setUnsaved({ heading: 'What was typed here could not be saved:', text });
    }
    buffer.remove(user.id, draftId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);

  // When the form turns read-only its focused control is disabled and focus would fall to
  // <body>; it goes to the notice that says why instead.
  useEffect(() => {
    if (readOnly && !wasReadOnly.current) {
      const active = document.activeElement;
      if (!active || active === document.body || formRef.current?.contains(active)) noticeRef.current?.focus();
    }
    wasReadOnly.current = readOnly;
  }, [readOnly]);

  // A save that failed only because the server could not be reached is tried again.
  const retrying = snapshot?.state === 'failed' && snapshot.transient === true;
  useEffect(() => {
    if (!retrying) return undefined;
    const retry = () => { void syncRef.current?.flush(); };
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    const timer = setInterval(retry, RETRY_MS);
    return () => {
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
      clearInterval(timer);
    };
  }, [retrying]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    Promise.all(
      CONFIG_REQUESTS.map(([key, path]) =>
        api
          .get(path, { signal: controller.signal })
          .then((response) => [key, response.data ?? [], null])
          .catch((error) => [key, [], error]),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next = { ...EMPTY_CONFIG, loaded: true, failed: [] };
      for (const [key, data, error] of results) {
        next[key] = data;
        if (error) next.failed.push(key);
      }
      setConfig(next);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // The parent ticket, when this draft was started from a ticket's (+). Loaded once and
  // copied over the new draft: its form and queue count as chosen, the owner stays the
  // agent's default (draft-screen.js `prefillTouched`).
  useEffect(() => {
    if (!prefillParent) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setParentState({ status: 'loading', error: null });
    api
      .get(`/tickets/${encodeURIComponent(prefillParent)}`, { signal: controller.signal })
      .then((parent) => {
        if (cancelled) return;
        const filled = prefillFromParent(parent, user);
        setCaller(filled.caller);
        // The initial fields FIRST, because this REPLACES the whole object rather than
        // merging into it. `prefillFromParent` returns only the fields it copies from the
        // parent, and `children` is not one of them -- deliberately, since a child of a
        // child is not a thing. Without the defaults underneath, `fields.children` became
        // undefined and the render below (`fields.children.map`) threw, so opening the (+)
        // on any ticket crashed the page outright.
        setFields({
          ...initialScreen(user).fields,
          ...filled.fields,
          parent: { id: parent.id, public_ref: parent.public_ref, subject: parent.subject },
        });
        setTouched(prefillTouched(filled.fields));
        setComposer(filled.composer);
        setParentState({ status: 'ready', error: null });
        subjectRef.current?.focus();
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setParentState({ status: 'error', error });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `user` is stable for the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillParent]);

  // The customer, when this draft was started from a customer's page: their organization,
  // store and record become the caller.
  useEffect(() => {
    if (!prefillCustomer) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setCustomerState({ status: 'loading', error: null });
    api
      .get(`/customers/${encodeURIComponent(prefillCustomer)}`, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        setCaller(callerFromCustomer(response.data));
        setCustomerState({ status: 'ready', error: null });
        subjectRef.current?.focus();
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setCustomerState({ status: 'error', error });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [prefillCustomer]);

  // The form and queue the agent has not chosen show today's defaults, on the main ticket
  // and on each child (against its own `touched`); a chosen one is never overwritten
  // (draft-screen.js `withConfigDefaults` and `withChildConfigDefaults`, which return the
  // same object when nothing changes, so this cannot loop).
  useEffect(() => {
    setFields((prev) => withChildConfigDefaults(withConfigDefaults(prev, touched, config), config));
  }, [config, touched, fields.form_id, fields.queue_id, fields.children]);

  // The organization's people serve two consumers — the requester list and the CC
  // picker — so they are fetched once here rather than twice below.
  const organizationId = caller.organization?.id ?? null;
  useEffect(() => {
    if (!organizationId) {
      setPeople({ orgId: null, rows: [], status: 'idle', error: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setPeople({ orgId: organizationId, rows: [], status: 'loading', error: null });

    api
      .get(`/organizations/${organizationId}/customers`, { signal: controller.signal })
      .then((response) => {
        if (!cancelled) setPeople({ orgId: organizationId, rows: response.data ?? [], status: 'ready', error: null });
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setPeople({ orgId: organizationId, rows: [], status: 'error', error });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [organizationId]);

  // The draft's store as it is now, inactive ones included, so a store retired since the
  // draft was started is caught before filing rather than refused by it (LOCATION_RETIRED).
  const locationId = caller.location?.id ?? null;
  useEffect(() => {
    setStoreRow(null);
    if (!organizationId || !locationId) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    api
      .get(`/organizations/${organizationId}/locations?include_inactive=true`, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        const row = (response.data ?? []).find((store) => String(store.id) === String(locationId));
        setStoreRow(row ? { id: row.id, is_active: row.is_active } : null);
        // A reopened draft's store arrives as `{ id, name }`; this row has the rest.
        if (row) setCaller((prev) => hydrateCaller(prev, { location: row }));
      })
      .catch(() => {
        // Unknown is not retired: filing re-checks the store on the server anyway.
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [organizationId, locationId]);

  // A reopened draft's customer arrives as `{ id, name }`: their record brings back the
  // email and phone the panels and macro placeholders read. Asked only for a thin slot, and
  // applied only while the slot still holds that customer (draft-screen.js `hydrateCaller`).
  const customerId = caller.customer?.id ?? null;
  const customerThin = callerNeedsDetail(caller).customer;
  useEffect(() => {
    if (!customerId || !customerThin) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    api
      .get(`/customers/${encodeURIComponent(customerId)}`, { signal: controller.signal })
      .then((response) => {
        if (!cancelled && response?.data) setCaller((prev) => hydrateCaller(prev, { customer: response.data }));
      })
      .catch(() => {
        // The name is still shown; the detail is a convenience.
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [customerId, customerThin]);

  const selectedForm = useMemo(
    () => config.forms.find((form) => form.id === fields.form_id) ?? null,
    [config.forms, fields.form_id],
  );
  // CONDITIONAL FIELDS (migration 029, imported from the desk's own forms). A field
  // named as the child of a condition stays hidden until its parent takes the value
  // that asks for it, and becomes required if the condition says so. Everything
  // downstream -- validation, encoding, submit -- reads THIS list rather than the form's
  // full one, so a hidden field cannot be required and cannot be sent.
  const formFields = useMemo(
    () => visibleFieldsWithRequirement(
      selectedForm?.fields ?? [], selectedForm?.conditions ?? [], fields.custom_fields),
    [selectedForm, fields.custom_fields],
  );

  // Choices this draft made that the configuration no longer has (a deleted queue, a
  // retired store, a field no longer on the form). Blocking ones hold Create back.
  const staleFindings = useMemo(
    () => staleChoicesFor({ caller, fields }, touched, config, storeRow),
    [caller, fields, touched, config, storeRow],
  );
  const blockedBy = blockingReason(staleFindings);

  /** Every change the agent makes to a field; choosing a form, queue or owner marks it as theirs. */
  function setField(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => addTouched(prev, key));
  }

  function validate() {
    const found = {};
    if (!caller.customer) found.customer = 'Choose who is calling before creating the ticket.';
    if (!composer.subject.trim()) found.subject = 'A subject is required.';
    if (!composer.is_internal_note && !user.can_send_customer_email) {
      found.message = 'Your role cannot send customer-facing email. Switch the message to an internal note.';
    }
    for (const field of missingRequiredFields(formFields, fields.custom_fields)) {
      found[`custom.${field.key}`] = `${field.label} is required on this form.`;
    }
    for (const field of invalidNumberFields(formFields, fields.custom_fields)) {
      found[`custom.${field.key}`] = `${field.label} must be a number.`;
    }
    // Each child is a whole ticket and is checked like one; its problems are keyed
    // `child.<key>.<field>` so the card can point at its own inputs and the summary
    // can say which child.
    fields.children.forEach((child, index) => {
      const childForm = config.forms.find((form) => form.id === child.fields.form_id) ?? null;
      for (const [key, message] of Object.entries(validateChild(child, childForm?.fields ?? [], user))) {
        found[`child.${child.key}.${key}`] = `Child ticket ${index + 1}: ${message}`;
      }
    });
    if (blockedBy) found.stale = blockedBy;
    return found;
  }

  /** The problems belonging to one child card, with the `child.<key>.` prefix removed. */
  function childErrors(child) {
    const prefix = `child.${child.key}.`;
    const own = {};
    for (const [key, message] of Object.entries(errors)) {
      if (key.startsWith(prefix)) own[key.slice(prefix.length)] = message.replace(/^Child ticket \d+: /, '');
    }
    return own;
  }

  function addChild() {
    // The child starts with the main ticket's `touched`: what is only displayed there is
    // only displayed on the child, and resolves at filing.
    const child = newChildDraft(fields, composer, nextChildKey(), touched);
    setFields((prev) => ({ ...prev, children: [...prev.children, child] }));
    // Focus the new card's subject once it has rendered — the agent is mid-call and
    // the next thing they type is what the second issue is.
    setTimeout(() => childSubjectRefs.current.get(child.key)?.focus(), 0);
  }

  function updateChild(next) {
    setFields((prev) => ({
      ...prev,
      children: prev.children.map((child) => (child.key === next.key ? next : child)),
    }));
  }

  function removeChild(key) {
    setFields((prev) => ({ ...prev, children: prev.children.filter((child) => child.key !== key) }));
  }

  // The main ticket's POST /api/tickets body. At filing, draft-flow.js `fileRequest`
  // removes the form, queue and owner the agent did not choose, so they resolve then.
  function buildBody(status, statusId) {
    const body = {
      subject: composer.subject.trim(),
      channel: fields.channel,
      priority: fields.priority,
      status,
      customer_id: caller.customer.id,
      tags: fields.tags,
    };

    // '' is how the API clears a type; an untouched select sends it too, which is null.
    body.type = fields.type || '';
    if (statusId) body.status_id = statusId;
    // Organization and location are denormalized onto the ticket at creation. Sent
    // explicitly because the agent's choice is the organization the call was raised
    // for, which can differ from the requester's current one (docs/API.md §3.3).
    if (caller.organization) body.organization_id = caller.organization.id;
    if (caller.location) body.location_id = caller.location.id;
    if (fields.queue_id) body.queue_id = fields.queue_id;
    // The server reads an absent key as "the creator" and an explicit null as "nobody —
    // let routing pick" (docs/API.md §3.3). Built here as the picker shows it; an owner the
    // agent never chose is then left out at filing, which is "whoever files".
    body.assigned_user_id = fields.assigned_user_id || null;
    if (fields.parent) body.parent_ticket_id = fields.parent.id;
    if (fields.form_id) body.form_id = fields.form_id;
    if (fields.device_serial.trim()) body.device_serial = fields.device_serial.trim();
    if (fields.follower_user_ids.length) body.follower_user_ids = fields.follower_user_ids;

    // A CC that is also the requester is 422 CC_IS_REQUESTER; the picker already
    // hides them, but a requester swapped after the CC was added would slip through.
    const ccs = fields.cc_customer_ids.filter((id) => id !== caller.customer.id);
    if (ccs.length) body.cc_customer_ids = ccs;

    // Only the answers to questions the form is actually asking. A value left behind by
    // a field that has since been hidden is an answer to a question nobody is being
    // asked -- Zendesk drops those too, and keeping one would file, say, a DoorDash
    // go-live date against a ticket whose integration was changed to something else.
    const custom = encodeCustomFields(formFields, valuesForVisibleFields(
      selectedForm?.fields ?? [], selectedForm?.conditions ?? [], fields.custom_fields));
    if (Object.keys(custom).length) body.custom_fields = custom;

    const bodyText = composer.body_text.trim();
    if (bodyText) {
      body.initial_message = {
        body_text: bodyText,
        is_internal_note: composer.is_internal_note,
      };
    }

    return body;
  }

  // FILING. The pending save is flushed first, so what is filed is what was saved (the
  // server files the revision named, docs/API.md §12.5), then the whole call is filed in
  // one request: the main ticket, every child, every link, or none of them.
  async function handleSubmit(override) {
    if (submitting || !canWrite || readOnly || phase !== 'ready' || !syncRef.current) return;

    const status = override?.status ?? fields.status;
    // An overridden status (the create-and-solve shortcut) drops the named status:
    // a name that rolls up to a different base is 422 STATUS_BASE_MISMATCH.
    const statusId = override ? null : fields.status_id;

    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      setSubmitError(null);
      errorRef.current?.focus();
      return;
    }

    setErrors({});
    setSubmitError(null);
    // The last edit goes to the controller, then nothing more until filing is over.
    pushEdit();
    submittingRef.current = true;
    setSubmitting(true);
    const stopSubmitting = () => {
      submittingRef.current = false;
      setSubmitting(false);
    };

    const sync = syncRef.current;
    try {
      const saved = await sync.flush();
      if (saved.state !== 'idle') {
        setSubmitError({ message: `Nothing was filed: the draft is not saved. ${syncNotice(saved)?.message ?? ''}`.trim() });
        stopSubmitting();
        errorRef.current?.focus();
        return;
      }

      const request = fileRequest({
        revision: saved.revision,
        parent: buildBody(status, statusId),
        touched,
        children: fields.children.map((child) => {
          const childForm = config.forms.find((form) => form.id === child.fields.form_id) ?? null;
          return {
            body: childBody(child, { caller, fields }, null, childForm?.fields ?? []),
            touched: childTouched(child),
          };
        }),
        links: fields.links,
      });
      const response = await api.post(`/ticket-drafts/${draftId}/file`, request);
      const ticket = response.data.ticket;

      finishedRef.current = true;
      sync.dispose();
      buffer.remove(user.id, draftId);
      reportStatus(draftId, null);
      void refresh();
      // Completion is keyed to THIS draft: the agent may have opened another tab meanwhile.
      if (mountedRef.current && isDraftAddress(window.location.pathname, draftId)) {
        navigate(supportPath(`/tickets/${ticket.public_ref}`));
      } else {
        announceFiled(ticket.public_ref);
      }
    } catch (error) {
      stopSubmitting();
      const closedNotice = noticeForError(error);
      if (closedNotice) {
        setClosed(closedNotice);
        reportStatus(draftId, null);
        return;
      }
      if (error?.code === 'DRAFT_STALE') {
        setUnsaved({ heading: 'Nothing was filed: this draft was changed on another PC, so it has been reloaded. Check it and create again.', text: '' });
        setLoadKey((key) => key + 1);
        return;
      }
      const mapped = mapFilingError(error, { children: fields.children, links: fields.links });
      setErrors(mapped.errors);
      setSubmitError({ message: mapped.banner });
      errorRef.current?.focus();
    }
  }

  // DISCARDING: confirmed first when there is anything to lose, then deleted on the
  // server, and the agent is taken to the next open draft (or the queue).
  function requestDiscard() {
    if (discarding || submitting || readOnly || phase !== 'ready') return;
    if (draftHasContent({ caller, fields, composer }, { defaultAssigneeId })) setConfirmingDiscard(true);
    else void discard();
  }

  async function discard() {
    if (discarding || submittingRef.current) return;
    setConfirmingDiscard(false);
    setDiscarding(true);
    setSubmitError(null);
    const sync = syncRef.current;
    // Nothing being thrown away is flushed on the way out.
    finishedRef.current = true;
    // A save in flight (the draft's first, perhaps) lands before the delete, so it cannot
    // create the draft again after it.
    if (sync?.getState().state === 'saving') await sync.flush();
    sync?.dispose();
    try {
      await api.del(`/ticket-drafts/${draftId}`);
    } catch (error) {
      // Never saved (404) or already discarded: there is nothing left to delete.
      const gone = error?.status === 404 || error?.code === 'DRAFT_DISCARDED';
      if (!gone) {
        const closedNotice = noticeForError(error);
        setDiscarding(false);
        if (closedNotice) {
          setClosed(closedNotice);
          reportStatus(draftId, null);
        } else {
          // Not discarded: read the draft again (the buffer still holds anything unsaved)
          // with a fresh controller, and say why.
          finishedRef.current = false;
          setSubmitError({ message: `The draft was not discarded: ${error?.message ?? 'something went wrong.'}` });
          setLoadKey((key) => key + 1);
        }
        return;
      }
    }
    buffer.remove(user.id, draftId);
    reportStatus(draftId, null);
    void refresh();
    navigate(nextDraftHref(drafts, draftId), { replace: true });
  }

  // THE OLD BROWSER SLOT (release day). Keep makes it this draft — the owner is never
  // counted as chosen, the form and queue only when they differ from today's default
  // (draft-screen.js `legacyKeep`) — and the effect above saves it. Either way it is deleted.
  const defaultFormId = config.forms.find((form) => form.is_default)?.id ?? '';
  const defaultQueueId = config.queues.find((queue) => queue.is_default)?.id ?? '';
  function keepLegacy() {
    if (!legacy || !legacyKeepReady(config)) return;
    applyScreen(mergeScreen(initialScreen(user), legacyKeep(legacy, {
      formId: defaultFormId, queueId: defaultQueueId, assigneeId: defaultAssigneeId,
    })));
    clearLegacySlot(storage);
    setLegacy(null);
  }

  function discardLegacy() {
    clearLegacySlot(storage);
    setLegacy(null);
  }

  // The shortcut handler is registered once and reads the latest submit through a
  // ref: re-binding a window listener on every keystroke would be its own bug.
  useEffect(() => {
    submitRef.current = handleSubmit;
  });

  useEffect(() => {
    function onKeyDown(event) {
      if (!(event.ctrlKey || event.metaKey) || event.key !== 'Enter') return;
      event.preventDefault();
      submitRef.current?.(event.shiftKey ? { status: 'solved' } : undefined);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function applyMacro(macro) {
    const filled = fillMacroTemplate(macro.body_template, {
      customer: caller.customer,
      organization: caller.organization,
      location: caller.location,
      user,
      subject: composer.subject,
    });

    setComposer((prev) => ({
      ...prev,
      body_text: prev.body_text.trim() ? `${prev.body_text.trimEnd()}\n\n${filled}` : filled,
    }));

    // ELEVEN VERBS (docs/API.md §5.1), all folding into the create request rather than
    // firing a call of their own.
    //
    // EVERY VERB THE SERVER ACCEPTS MUST HAVE A BRANCH HERE. This chain silently skips
    // what it does not recognise, so a verb added to ACTION_TYPES and forgotten here
    // produces a macro that applies, does nothing, and keeps doing nothing forever with
    // no log and no symptom. `test/web/macro-apply.test.js` asserts the two lists match.
    for (const action of macro.actions ?? []) {
      // Clear `status_id` alongside it, exactly as the status picker does at the
      // `base:` branch below. A macro's `set_status` names a BASE status — the action
      // vocabulary has no `status_id` slot at all — so leaving a named status selected
      // left the two disagreeing, and `assertStatusRollsUp` refused the create with
      // `That named status rolls up to "on_hold", not "open"`. Every seeded macro 422'd
      // on top of a named status, and the agent had no way to see why: the macro looked
      // applied, because the body text and every other field had already changed.
      if (action.type === 'set_status') {
        setFields((prev) => ({ ...prev, status: action.value, status_id: null }));
      }
      else if (action.type === 'set_priority') setField('priority', action.value);
      else if (action.type === 'set_type') setField('type', action.value);
      // `current_user` is resolved HERE, at apply time, which is the whole point of it:
      // the macro means "assign this to me", and who "me" is depends on who clicked.
      else if (action.type === 'assign') {
        setField('assigned_user_id', action.value === 'current_user'
          ? assigneeFor(user) : String(action.value));
      }
      else if (action.type === 'add_tag') {
        setFields((prev) =>
          prev.tags.includes(action.value) ? prev : { ...prev, tags: [...prev.tags, action.value] },
        );
      }
      // REPLACES the list, which is what Zendesk's `set_tags` means and why it is not
      // `add_tag` in a loop. Space separated, the encoding the desk itself uses.
      else if (action.type === 'set_tags') {
        setFields((prev) => ({
          ...prev,
          tags: String(action.value).split(/\s+/).filter(Boolean),
        }));
      }
      // The mirror of `set_status`: that one clears `status_id`, so this one sets it
      // and leaves `status` for the server to roll up. Setting both from a macro is
      // how you get `assertStatusRollsUp` refusing the create.
      else if (action.type === 'set_named_status') {
        setField('status_id', String(action.value));
      }
      else if (action.type === 'set_queue') setField('queue_id', String(action.value));
      else if (action.type === 'set_form') setField('form_id', String(action.value));
      else if (action.type === 'set_subject') {
        setComposer((prev) => ({ ...prev, subject: String(action.value) }));
      }
      else if (action.type === 'add_follower') {
        const followerId = action.value === 'current_user'
          ? (user?.id ? String(user.id) : '') : String(action.value);
        if (followerId) {
          setFields((prev) => (prev.follower_user_ids.includes(followerId)
            ? prev
            : { ...prev, follower_user_ids: [...prev.follower_user_ids, followerId] }));
        }
      }
      // Keyed by `custom_field_defs.key`, which is the same shape the create request
      // sends, so nothing has to be translated on submit.
      else if (action.type === 'set_custom_field' && action.field) {
        setFields((prev) => ({
          ...prev,
          custom_fields: { ...prev.custom_fields, [action.field]: action.value },
        }));
      }
    }
  }

  const customFieldErrors = useMemo(() => {
    const mapped = {};
    for (const [key, message] of Object.entries(errors)) {
      if (key.startsWith('custom.')) mapped[key.slice(7)] = message;
    }
    return mapped;
  }, [errors]);

  const errorList = Object.values(errors);
  const statusValue = fields.status_id ? `named:${fields.status_id}` : `base:${fields.status}`;
  const isResolving = fields.status === 'solved' || fields.status === 'closed';

  const ccOptions = people.rows
    .filter((person) => person.id !== caller.customer?.id)
    .map((person) => ({
      id: person.id,
      label: person.name ?? person.email,
      hint: person.location_name ?? person.email,
    }));

  if (!canWrite) {
    return (
      <div className="rounded-lg border border-line bg-surface py-16 text-center">
        <h1 className="text-base font-semibold text-ink">New ticket</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Your role is read-only, so ticket creation is not available.
        </p>
      </div>
    );
  }

  const hasContent = draftHasContent({ caller, fields, composer }, { defaultAssigneeId });
  // The old slot's offer is withdrawn once this draft has content or a save has landed:
  // Keep would overwrite it.
  const showLegacy = Boolean(legacy) && !hasContent && !((snapshot?.revision ?? 0) > 0);
  const notices = (
    <DraftNotices
      notice={notice}
      unsaved={unsaved}
      onDismissUnsaved={() => setUnsaved(null)}
      legacy={showLegacy ? legacy : null}
      legacyReady={legacyKeepReady(config)}
      noticeRef={noticeRef}
      onKeepLegacy={keepLegacy}
      onDiscardLegacy={discardLegacy}
      findings={staleFindings}
      // True only when the draft existed on the server when this screen opened. Not the
      // revision: a new draft autosaves to revision 1 within a moment of being started.
      wasSaved={loadedFromServer}
    />
  );

  if (phase !== 'ready') {
    return (
      <div>
        <h1 className="text-lg font-semibold text-ink">New ticket</h1>
        {notices}
        {phase === 'loading' && <p className="mt-3 text-sm text-ink-muted">Loading the draft…</p>}
        {phase === 'error' && (
          <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            Could not load this draft{loadError?.message ? `: ${loadError.message}` : '.'} Anything typed
            here is still kept in this browser.{' '}
            <button type="button" className="font-medium underline" onClick={() => setLoadKey((key) => key + 1)}>
              Try again
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    <form
      ref={formRef}
      // WHILE FILING the form takes no input, and focus stays where it is: typing is
      // cancelled before it lands, and a change, key or button press is stopped before the
      // control's own handler runs (a controlled input or select then snaps back to state).
      onBeforeInputCapture={(event) => { if (submittingRef.current) event.preventDefault(); }}
      onChangeCapture={(event) => { if (submittingRef.current) event.stopPropagation(); }}
      onKeyDownCapture={(event) => { if (submittingRef.current && event.key !== 'Tab') event.stopPropagation(); }}
      onClickCapture={(event) => {
        if (submittingRef.current && event.target instanceof Element && event.target.closest('button')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
      onKeyDown={(event) => {
        // Enter in a single-line field must not create the ticket: the account
        // number, the requester filter and the tag box all end in Enter, and an
        // implicit submit there would file a blank ticket mid-sentence.
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
          event.preventDefault();
        }
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-lg font-semibold text-ink">
          {fields.parent ? 'New child ticket' : fields.children.length ? `New tickets (${fields.children.length + 1})` : 'New ticket'}
        </h1>
        <p className="text-xs text-ink-muted">
          <kbd className="rounded border border-line-strong px-1">Ctrl</kbd>+
          <kbd className="rounded border border-line-strong px-1">Enter</kbd> create ·{' '}
          <kbd className="rounded border border-line-strong px-1">Ctrl</kbd>+
          <kbd className="rounded border border-line-strong px-1">Shift</kbd>+
          <kbd className="rounded border border-line-strong px-1">Enter</kbd> create &amp; solve ·{' '}
          <kbd className="rounded border border-line-strong px-1">Alt</kbd>+
          <kbd className="rounded border border-line-strong px-1">N</kbd> new contact
        </p>
      </div>

      {fields.parent && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-line-strong bg-sunken px-3 py-2 text-sm text-ink-secondary">
          <span className="min-w-0">
            Sub-issue of{' '}
            <Link to={supportPath(`/tickets/${fields.parent.public_ref}`)} className="font-mono text-ink hover:underline">
              #{fields.parent.public_ref}
            </Link>
            {' '}— <span className="text-ink">{fields.parent.subject}</span>. Everything editable was
            copied from it; change what differs. It is worked and closed on its own.
          </span>
          {!readOnly && (
            <button
              type="button"
              className="font-medium hover:underline"
              onClick={() => setField('parent', null)}
              title="Create this as a standalone ticket instead"
            >
              Detach
            </button>
          )}
        </div>
      )}

      {parentState.status === 'loading' && (
        <div className="mt-3 rounded-md border border-line-strong bg-sunken px-3 py-2 text-sm text-ink-secondary">
          Copying ticket #{prefillParent}…
        </div>
      )}

      {parentState.status === 'error' && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          Could not load ticket #{prefillParent} to copy from
          {parentState.error?.message ? `: ${parentState.error.message}` : '.'} This ticket will be
          created on its own.
        </div>
      )}

      {customerState.status === 'loading' && (
        <div className="mt-3 rounded-md border border-line-strong bg-sunken px-3 py-2 text-sm text-ink-secondary">
          Loading the caller…
        </div>
      )}

      {customerState.status === 'error' && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          Could not load the customer
          {customerState.error?.message ? `: ${customerState.error.message}` : '.'} Choose who is
          calling below.
        </div>
      )}

      {notices}

      {config.failed.length > 0 && (
        <div className="mt-3 rounded-md border border-line-strong bg-sunken px-3 py-2 text-sm text-ink-secondary">
          Some options could not be loaded ({config.failed.join(', ')}). The ticket can still be
          created without them.
        </div>
      )}

      {/* Same explicit red pair as the queue's error card (List.jsx): there is no
          semantic error surface in the token set, and a 50/300/800 light card has to
          become a 950/900/200 dark one — a direction change, not a tint. */}
      <div
        ref={errorRef}
        tabIndex={-1}
        aria-live="polite"
        className={
          errorList.length || submitError
            ? 'mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950'
            : 'sr-only'
        }
      >
        {submitError && (
          <p className="text-sm font-medium text-red-800 dark:text-red-200">{submitError.message}</p>
        )}
        {errorList.length > 0 && (
          <ul className="list-inside list-disc text-sm text-red-800 dark:text-red-200">
            {errorList.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
      </div>

      {/* READ-ONLY once the draft cannot be saved from here (taken over, filed or discarded
          elsewhere, or no longer this person's): a disabled fieldset disables every control
          inside it at once. Reset to no border, margin or padding, and min-width 0, which a
          fieldset otherwise sets to its content's and which would break the grid below. */}
      <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">

      {/* grid-rows-[auto_1fr], and it is load-bearing: the middle column spans both
          rows, and with two auto rows its height was shared between them, so a tall
          composer — several child tickets — stretched row 1 and pushed Classification
          down to the middle of the page. Items spanning an fr track are ignored when the
          auto row is sized, so row 1 is exactly the Caller panel's height and
          Classification stays directly beneath it however long the middle gets. */}
      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[22rem_minmax(0,1fr)_17rem] lg:grid-rows-[auto_1fr]">
        <div className="lg:col-start-1 lg:row-start-1">
          <CallerPanel
            value={caller}
            onChange={setCaller}
            onRequesterChosen={() => subjectRef.current?.focus()}
            searchRef={searchRef}
            people={people}
          />
        </div>

        <div className="space-y-4 lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <Composer
            value={composer}
            onChange={setComposer}
            subjectRef={subjectRef}
            canSendCustomerEmail={user.can_send_customer_email}
            macros={config.macros}
            onApplyMacro={applyMacro}
            errors={errors}
          />

          {/* Other issues from the same call, each its own ticket, filed as children of
              the one above when everything is created together (child-tickets.js). A
              child of a child is not a thing (nesting is one level), so the (+) is not
              offered while THIS ticket is itself being created as a child. */}
          {fields.children.map((child, index) => (
            <ChildTicketCard
              key={child.key}
              child={child}
              index={index}
              config={config}
              user={user}
              errors={childErrors(child)}
              onChange={updateChild}
              onRemove={() => removeChild(child.key)}
              subjectRef={(node) => {
                if (node) childSubjectRefs.current.set(child.key, node);
                else childSubjectRefs.current.delete(child.key);
              }}
            />
          ))}

          {!fields.parent && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn-ghost flex shrink-0 items-center gap-2 whitespace-nowrap ring-1 ring-line-strong"
                onClick={addChild}
                aria-label="Add another issue from this call as a child ticket"
                title="Another issue from this call — files as a child ticket of this one"
              >
                <span className="text-lg leading-none" aria-hidden="true">+</span>
                <span className="text-sm">Another issue from this call</span>
              </button>
              <span className="text-xs text-ink-muted">
                Adds a child ticket, pre-filled from this one. Each is worked and closed on its own.
              </span>
            </div>
          )}
        </div>

        <section className="grid gap-3 rounded-lg border border-line bg-surface p-4 lg:col-start-1 lg:row-start-2">
          <h2 className="text-sm font-semibold text-ink">Classification</h2>

          <div>
            <label className="field-label" htmlFor="ticket-form">Form</label>
            <select
              id="ticket-form"
              className="field-input"
              value={fields.form_id}
              onChange={(event) => setField('form_id', event.target.value)}
            >
              <option value="">-</option>
              {config.forms.map((form) => (
                <option key={form.id} value={form.id}>{form.name}</option>
              ))}
            </select>
          </div>

          {/* Which fields render is driven by the chosen form (SPEC.md §9). */}
          <CustomFields
            fields={formFields}
            values={fields.custom_fields}
            errors={customFieldErrors}
            onChange={(next) => setField('custom_fields', next)}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label" htmlFor="ticket-priority">Priority</label>
              <select
                id="ticket-priority"
                className="field-input"
                value={fields.priority}
                onChange={(event) => setField('priority', event.target.value)}
              >
                {PRIORITIES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="ticket-type">Type</label>
              <select
                id="ticket-type"
                className="field-input"
                value={fields.type ?? ''}
                onChange={(event) => setField('type', event.target.value)}
              >
                <option value="">Not categorised</option>
                {typeOptionsFor(fields.type).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {/* NO CHANNEL PICKER. This screen exists because somebody rang the desk,
                so the answer is always `phone` — it is still sent (draft-screen.js EMPTY_FIELDS keeps
                `channel: 'phone'`), just not asked for. Email tickets do not come
                through here at all; ingestion sets `channel: 'email'` itself.

                Offering the choice invited an agent to mislabel a call as email, which
                is not cosmetic: `channel` is a routing condition and a reporting
                dimension, and SPEC.md §2.1's phone-first volume is the number
                leadership compares across the cutover. A dropdown whose only correct
                answer is the default is a way to get that number wrong. */}
          </div>

          <div>
            <label className="field-label" htmlFor="ticket-assignee">Assignee</label>
            <div className="flex gap-2">
              <select
                id="ticket-assignee"
                className="field-input"
                value={fields.assigned_user_id}
                onChange={(event) => setField('assigned_user_id', event.target.value)}
              >
                <option value="">Unassigned</option>
                {config.assignees.map((option) => (
                  <option key={option.id} value={option.id}>{option.display_name}</option>
                ))}
              </select>
              {/* "take it", carried over from the Zendesk screen (SPEC.md §6.2).
                  Only offered to users who may own a ticket — the server and the
                  database reject the rest with 403 ASSIGNEE_NOT_ASSIGNABLE. */}
              {user.can_be_assigned && (
                <button
                  type="button"
                  className="btn-ghost shrink-0 whitespace-nowrap"
                  onClick={() => setField('assigned_user_id', user.id)}
                >
                  Take it
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="ticket-queue">Queue</label>
            <select
              id="ticket-queue"
              className="field-input"
              value={fields.queue_id}
              onChange={(event) => setField('queue_id', event.target.value)}
            >
              <option value="">Default</option>
              {config.queues.map((queue) => (
                <option key={queue.id} value={queue.id}>{queue.name}</option>
              ))}
            </select>
          </div>

          <TagInput
            value={fields.tags}
            onChange={(next) => setField('tags', next)}
            suggestions={config.tags}
          />

          <div>
            <label className="field-label" htmlFor="ticket-device-serial">Device serial</label>
            <input
              id="ticket-device-serial"
              className="field-input"
              autoComplete="off"
              value={fields.device_serial}
              onChange={(event) => setField('device_serial', event.target.value)}
            />
          </div>

          <div className="flex items-end gap-2">
            <div className="min-w-0 grow">
              <ChipSelect
                id="ticket-followers"
                label="Followers"
                placeholder="Add a colleague…"
                hint="Anyone can follow. Hardware and implementation get looped in this way."
                options={config.users.map((option) => ({
                  id: option.id,
                  label: option.display_name,
                  hint: option.role,
                }))}
                value={fields.follower_user_ids}
                onChange={(next) => setField('follower_user_ids', next)}
              />
            </div>
            <button
              type="button"
              className="btn-ghost shrink-0 whitespace-nowrap"
              onClick={() =>
                setFields((prev) =>
                  prev.follower_user_ids.includes(user.id)
                    ? prev
                    : { ...prev, follower_user_ids: [...prev.follower_user_ids, user.id] },
                )
              }
            >
              Follow
            </button>
          </div>

          {/* CCs can only be set at creation in v1 (docs/API.md §7), so the one
              chance to record the colleague the caller wants copied is here. */}
          <ChipSelect
            id="ticket-ccs"
            label="CC"
            placeholder={caller.organization ? 'Copy someone else…' : 'Choose an organization first'}
            hint="External CCs receive the customer-facing replies on this ticket."
            options={ccOptions}
            value={fields.cc_customer_ids}
            onChange={(next) => setField('cc_customer_ids', next)}
          />

          {/* Related tickets (docs/API.md §3.7): "about the same thing", no behaviour.
              Collected here and linked once the ticket has a number. */}
          <div>
            <label className="field-label" htmlFor="ticket-links">Related tickets</label>
            <TicketLinkPicker
              inputId="ticket-links"
              excludeIds={fields.links.map((link) => link.id)}
              onPick={(picked) =>
                setFields((prev) =>
                  prev.links.some((link) => link.id === picked.id)
                    ? prev
                    : { ...prev, links: [...prev.links, { id: picked.id, public_ref: picked.public_ref, subject: picked.subject }] },
                )
              }
            />
            {fields.links.length > 0 && (
              <ul className="mt-2 space-y-1">
                {fields.links.map((link, index) => (
                  <li key={link.id} className="text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate">
                        <span className="font-mono text-xs text-ink-muted">#{link.public_ref}</span>{' '}
                        <span className="text-ink">{link.subject}</span>
                      </span>
                      <button
                        type="button"
                        className="shrink-0 text-xs text-ink-secondary hover:underline"
                        onClick={() => setField('links', fields.links.filter((other) => other.id !== link.id))}
                      >
                        Remove
                      </button>
                    </div>
                    {/* A refusal from filing, at `links[index]` (draft-flow.js `mapFilingError`). */}
                    {errors[`link.${index}`] && (
                      <p className="mt-0.5 text-xs text-red-700 dark:text-red-300">
                        {errors[`link.${index}`].replace(/^Related ticket #\S+: /, '')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-ink-muted">
              Type a ticket number or words from its subject and pick from the matches.
            </p>
          </div>
        </section>

        <div className="lg:col-start-3 lg:row-start-1 lg:row-span-2">
          <CustomerContext
            organization={caller.organization}
            location={caller.location}
            customer={caller.customer}
          />
        </div>
      </div>

      {/* The action bar floats over scrolled content, so it needs its own elevation.
          On white a 1px rule plus a faint shadow is enough; on a dark page a
          near-black shadow does nothing against a near-black page, so the dark step
          leans on a heavier, wider shadow to keep the bar reading as "in front of"
          rather than "part of" the form. */}
      <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-t-lg border-t border-line bg-surface/95 px-3 py-3 shadow-[0_-1px_3px_rgba(15,23,42,0.08)] backdrop-blur dark:shadow-[0_-2px_8px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-2 text-xs">
          {/* SPEC.md §7.4 — this chip is the last thing an agent sees before the
              ticket is filed, and it is the only place on this screen that says
              whether the opening message reaches the customer. So it uses the note
              tokens at full strength: a filled amber-500 chip with near-black text
              (8.7:1) in BOTH themes for a note, against the cool reply pair
              (bg-reply-strong / text-reply-ink, 10:1 light and 11.1:1 dark) for a
              public reply. The two differ by hue, by fill weight and by text
              lightness, so they stay distinguishable in dark, in greyscale, and to a
              colour-blind reader — never two similar dark greys. */}
          <span
            className={`badge ${
              composer.is_internal_note
                ? 'bg-note-accent text-note-on-accent ring-note-accent font-semibold uppercase tracking-wide'
                : 'bg-reply-strong text-reply-ink ring-reply-accent'
            }`}
          >
            {composer.is_internal_note ? 'Internal note' : 'Public reply: emailed'}
          </span>
          {((snapshot?.revision ?? 0) > 0 || hasContent) && (
            <button
              type="button"
              className="text-ink-muted hover:underline"
              aria-disabled={discarding || submitting}
              onClick={requestDiscard}
            >
              {discarding ? 'Discarding…' : 'Discard draft'}
            </button>
          )}
          {/* Quiet, and polite: "Saved" / "Saving…" / "Not saved". What a failure means is
              said in the notice above the form. */}
          <span className="text-ink-muted" aria-live="polite">{saveStatusLabel(snapshot)}</span>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-ink-secondary" htmlFor="submit-status">
            Submit as
          </label>
          {/* "Submit as <status>" is the create-and-solve requirement (SPEC.md §6):
              a call resolved on the call must not need creating then reopening. */}
          <select
            id="submit-status"
            className="field-input w-44 py-1.5 text-sm"
            value={statusValue}
            onChange={(event) => {
              const [kind, value] = event.target.value.split(':');
              if (kind === 'base') {
                setFields((prev) => ({ ...prev, status: value, status_id: null }));
                return;
              }
              const named = config.statuses.find((status) => status.id === value);
              if (named) {
                setFields((prev) => ({ ...prev, status: named.base_status, status_id: named.id }));
              }
            }}
          >
            {BASE_STATUSES.map(([value, label]) => (
              <option key={value} value={`base:${value}`}>{label}</option>
            ))}
            {config.statuses.length > 0 && (
              <optgroup label="Named statuses">
                {config.statuses.map((status) => (
                  <option key={status.id} value={`named:${status.id}`}>
                    {status.name} ({status.base_status.replace('_', ' ')})
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {/* aria-disabled rather than disabled, so the button stays focusable and says why:
              handleSubmit returns early while busy, and lists what blocks it. */}
          <button
            type="submit"
            className="btn-primary aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
            aria-disabled={submitting || Boolean(blockedBy)}
            aria-describedby={blockedBy ? 'create-blocked' : undefined}
          >
            {createLabel(fields.children.length, { resolving: isResolving, submitting })}
          </button>
        </div>
        {blockedBy && (
          <p id="create-blocked" className="w-full text-right text-xs text-red-700 dark:text-red-300">{blockedBy}</p>
        )}
      </div>
      </fieldset>
    </form>

    {/* Outside the form: the dialog is portalled to <body>, but React events still bubble
        through the component tree, and the form's own Enter handling must not see them. */}
    {confirmingDiscard && (
      <Modal title="Discard this draft?" onClose={() => setConfirmingDiscard(false)} initialFocusRef={keepEditingRef}>
        <div className="px-4 py-3 text-sm text-ink-secondary">
          <p>Everything typed in this draft will be deleted. This cannot be undone.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" ref={keepEditingRef} className="btn-ghost" onClick={() => setConfirmingDiscard(false)}>
              Keep editing
            </button>
            <button type="button" className="btn-primary" onClick={() => { void discard(); }}>
              Discard
            </button>
          </div>
        </div>
      </Modal>
    )}
    </>
  );
}
